import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import type {
  TransportRoute,
  UpdateTransportRoutePayload,
} from 'contracts/transport-routes';
import { useTranslation } from 'hooks';
import {
  useCarrierOptions,
  useYesOrNoOptions,
} from 'hooks/api/options';
import {
  useBackwardTripState,
} from 'pages/Settings/transport-routes/hooks';
import { Card, CardContent } from 'components/ui/card';
import { Form, FormField } from 'components/ui/form';
import { InputGs, SelectGs, ComboboxGs } from 'components/ui/Forms';
import {
  TripManager,
  type TripManagerRef,
} from 'pages/Settings/transport-routes/shared-components';

import { formSchema, type FormSchema } from './form-schema';
import { translations } from './translations';

type Ref = {
  getFormData: () => Promise<UpdateTransportRoutePayload>
}

type Props = React.HTMLAttributes<HTMLDivElement> & {
  transportRoute?: TransportRoute;
  loading?: boolean;
}

export type UpdateRouteFormRef = Ref

export const UpdateRouteForm = React.forwardRef<Ref, Props>(({
                                                               transportRoute,
                                                               loading,
                                                               ...props
                                                             }, ref) => {
  const { getTranslation: t } = useTranslation(translations);
  const carrierOptions = useCarrierOptions();
  const yesOrNoOptions = useYesOrNoOptions();
  const { clearBackwardTripState } = useBackwardTripState();

  const [hasBackwardTrip, setHasBackwardTrip] = React.useState<boolean | undefined>();

  const forwardTripManagerRef = React.useRef<TripManagerRef>(null);
  const backwardTripManagerRef = React.useRef<TripManagerRef>(null);

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
  });

  const hydrateFormValues = React.useCallback(() => {
    if (!transportRoute) return;

    const { name, carrier_id, backward_trip } = transportRoute;

    if (name) form.setValue('name', name);
    if (carrier_id) form.setValue('carrier_id', carrier_id.toString());
    form.setValue('backward_trip', backward_trip ? '1' : '0');
    setHasBackwardTrip(!!backward_trip);
  }, [transportRoute]);

  const sendDataToTripManagers = React.useCallback(() => {
    if (!transportRoute || form.getFieldState('backward_trip').isDirty) return;

    if (transportRoute?.forward_trip) {
      forwardTripManagerRef.current?.setTripData(transportRoute.forward_trip);
    }

    if (transportRoute?.backward_trip) {
      backwardTripManagerRef.current?.setTripData(transportRoute.backward_trip);
    }
  }, [transportRoute, hasBackwardTrip, form]);

  const getFormValues = React.useCallback(async (): Promise<FormSchema> => {
    const isFormValid = await form.trigger();

    if (!isFormValid) {
      throw new Error('Update route form has invalid data');
    }

    return form.getValues();
  }, [form]);

  const getFormData = React.useCallback(async (): Promise<UpdateTransportRoutePayload> => {
    try {
      const [
        formValues,
        forwardTripData,
        backwardTripData,
      ] = await Promise.all([
        getFormValues(),
        forwardTripManagerRef.current?.getFormData()!,
        hasBackwardTrip ? backwardTripManagerRef.current?.getFormData()! : null,
      ]);

      return {
        name: formValues.name,
        carrier_id: +formValues.carrier_id,
        forward_trip: forwardTripData,
        backward_trip: backwardTripData,
      };
    } catch (e: any) {
      throw new Error(e.message);
    }
  }, [getFormValues, hasBackwardTrip]);

  React.useEffect(() => {
    hydrateFormValues();
  }, [hydrateFormValues]);

  React.useEffect(() => {
    sendDataToTripManagers();
  }, [sendDataToTripManagers]);

  React.useEffect(() => {
    if (hasBackwardTrip !== undefined) {
      clearBackwardTripState();
    }
  }, [hasBackwardTrip, clearBackwardTripState]);

  React.useImperativeHandle(ref, () => ({
    getFormData,
  }), [getFormData]);

  return (
    <div {...props}>
      <Card>
        <CardContent className={'pt-6'}>
          <Form {...form}>
            <form>
              <div className={'grid grid-cols-4 gap-4'}>
                <FormField
                  name={'name'}
                  control={form.control}
                  render={({ field }) => (
                    <InputGs {...field} label={t('name')} />
                  )}
                />
                <FormField
                  control={form.control}
                  name={'carrier_id'}
                  render={({ field }) => (
                    <ComboboxGs
                      label={t('carrier')}
                      form={form}
                      fieldName={field.name}
                      fieldValue={field.value}
                      content={carrierOptions.singleSelectOptions}
                      loading={carrierOptions.loading}
                    />
                  )}
                />
                <FormField
                  name={'backward_trip'}
                  control={form.control}
                  render={({ field }) => (
                    <SelectGs
                      {...field}
                      label={t('freightReturn')}
                      onValueChange={(v) => {
                        if (!v.trim()) return;
                        field.onChange(v);
                        setHasBackwardTrip(v === '1');
                      }}
                      content={yesOrNoOptions.singleSelectOptions}
                    />
                  )}
                />
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
      {hasBackwardTrip !== undefined && (
        <TripManager
          ref={forwardTripManagerRef}
          tripDirection={'forward'}
          title={hasBackwardTrip
            ? t('forwardTripTitle')
            : t('forwardTripOnlyTitle')}
          className={'mt-6'}
        />
      )}
      {hasBackwardTrip === true && (
        <TripManager
          ref={backwardTripManagerRef}
          tripDirection={'backward'}
          title={t('backwardTripTitle')}
          className={'mt-6'}
        />
      )}
    </div>
  );
});
UpdateRouteForm.displayName = 'CreateRouteForm';